import { Minus } from 'lucide-react';
import clsx from 'clsx';

import { pluralize } from '@/react/common/string-utils';

import { PolicyType, isK8sPolicy } from '../../types';

type GroupItemVariant = 'new' | 'conflict' | 'detached';

/**
 * Unified component for displaying environment group items in the policy preview modal.
 * Supports three visual variants: new (blue), conflict (warning/amber), and detached (gray).
 */
interface GroupItemProps {
  /** Visual variant determining colors and layout */
  variant: GroupItemVariant;
  /** Name of the environment group */
  groupName: string;
  /** Unique ID for the environment group */
  groupId: number;
  /** Number of environments in this group */
  environmentCount: number;
  /** Number of supported environments (optional, for new/conflict variants) */
  supportedEnvironments?: number;
  /** Number of unsupported environments (optional, for new/conflict variants) */
  unsupportedEnvironments?: number;
  /** Type of policy being applied (used to determine environment type labels) */
  policyType?: PolicyType;
  /** Subtitle text displayed below the group name */
  subtitle?: string;
}

const variantStyles: Record<
  GroupItemVariant,
  {
    container: string;
    title: string;
    subtitle: string;
    count: string;
    note: string;
  }
> = {
  new: {
    container:
      'rounded border border-blue-4 bg-blue-2 p-2 th-dark:bg-blue-10 th-dark:border-blue-8 th-highcontrast:bg-blue-10 th-highcontrast:border-white',
    title:
      'font-medium text-blue-8 th-dark:text-white th-highcontrast:text-white',
    subtitle:
      'mt-1 text-sm text-blue-7 th-dark:text-white th-highcontrast:text-white',
    count:
      'ml-2 text-sm font-normal text-blue-7 th-dark:text-blue-4 th-highcontrast:text-blue-4',
    note: 'mt-0.5 text-xs text-blue-6 th-dark:text-blue-5 th-highcontrast:text-blue-5 italic',
  },
  conflict: {
    container:
      'rounded border border-warning-4 bg-warning-2 p-2 th-dark:bg-warning-10 th-dark:border-warning-8 th-highcontrast:bg-warning-10 th-highcontrast:border-white',
    title:
      'font-medium text-warning-8 th-dark:text-white th-highcontrast:text-white',
    subtitle:
      'mt-1 text-sm text-warning-7 th-dark:text-white th-highcontrast:text-white',
    count:
      'ml-2 text-sm font-normal text-warning-7 th-dark:text-warning-4 th-highcontrast:text-warning-4',
    note: 'mt-0.5 text-xs text-warning-6 th-dark:text-warning-5 th-highcontrast:text-warning-5 italic',
  },
  detached: {
    container:
      'rounded border border-gray-4 bg-gray-2 p-2 th-dark:bg-gray-10 th-dark:border-gray-8 th-highcontrast:bg-gray-10 th-highcontrast:border-white',
    title:
      'font-medium text-gray-8 th-dark:text-white th-highcontrast:text-white',
    subtitle:
      'ml-6 mt-1 text-sm text-gray-6 th-dark:text-gray-4 th-highcontrast:text-gray-4',
    count:
      'ml-2 text-sm font-normal text-gray-6 th-dark:text-gray-4 th-highcontrast:text-gray-4',
    note: 'ml-6 mt-0.5 text-xs text-gray-5 th-dark:text-gray-5 th-highcontrast:text-gray-5 italic',
  },
};

export function GroupItem({
  variant,
  groupName,
  groupId,
  environmentCount,
  supportedEnvironments,
  unsupportedEnvironments,
  policyType,
  subtitle,
}: GroupItemProps) {
  const styles = variantStyles[variant];
  const { countText, noteText } = formatEnvironmentCount(
    supportedEnvironments ?? environmentCount,
    unsupportedEnvironments,
    policyType
  );

  return (
    <li
      data-cy={`${variant}-group-item-${groupId}`}
      className={styles.container}
    >
      <div
        className={clsx(variant === 'detached' && 'flex items-center gap-2')}
      >
        {variant === 'detached' && (
          <Minus className="h-4 w-4 text-gray-6 th-dark:text-gray-4" />
        )}
        <div className={styles.title}>
          {groupName}
          <span className={styles.count}>{countText}</span>
        </div>
      </div>
      {noteText && <div className={styles.note}>{noteText}</div>}
      {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
    </li>
  );
}

/**
 * Formats the environment count for display.
 * Shows only supported environments when there's a mix, with a note about skipped ones.
 *
 * Note: We can't assume unsupported environments are the "opposite" type (e.g., Docker for K8s policies)
 * because some environments of the same platform type may also be unsupported (e.g., K8s edge async
 * environments are not supported by K8s policies).
 */
function formatEnvironmentCount(
  supported?: number,
  unsupported?: number,
  policyType?: PolicyType
): { countText: string; noteText: string | null } {
  // If we have breakdown info and there are unsupported environments, show friendly message
  if (
    supported !== undefined &&
    unsupported !== undefined &&
    unsupported > 0 &&
    policyType
  ) {
    const isK8s = isK8sPolicy(policyType);
    const targetType = isK8s ? 'Kubernetes' : 'Docker';

    return {
      countText: `(${supported} ${targetType} ${pluralize(
        supported,
        'environment'
      )})`,
      noteText: `${unsupported} unsupported ${pluralize(
        unsupported,
        'environment'
      )} in this group won't be affected`,
    };
  }

  // Default: just show the total (use supported if available, otherwise fall back)
  const count = supported ?? 0;
  return {
    countText: `(${count} ${pluralize(count, 'environment')})`,
    noteText: null,
  };
}
